import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  Typography,
  Tooltip,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  Paper
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { fNumber } from "../utils/formatNumber";

const STORAGE_KEY = "mtc_calculator_data";

// Parse comma-separated or raw string into clean float
const parseNum = (v) => {
  if (v == null || v === "") return 0;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

// Formats input with thousands commas as user types, preserving up to 2 decimals
const formatInputWithCommas = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const clean = String(val).replace(/[^0-9.]/g, "");
  const parts = clean.split(".");
  const intPart = parts[0] ? Number(parts[0]).toLocaleString("en-US") : "";
  if (parts.length > 1) {
    return `${intPart}.${parts[1].slice(0, 2)}`;
  }
  return intPart;
};

// Formats number to standard currency string e.g. "4,000,000.00" on blur
const formatToCurrencyString = (val) => {
  const n = parseNum(val);
  if (n === 0 && (!val || String(val).trim() === "")) return "";
  return fNumber(n, 2);
};

export default function SmallCalculator({
  initialAmount = "",
  onApplyShares,    // (sharesAmount:number) => void
  onApplySavings,   // (savingsAmount:number) => void
  onApplyLoan,      // (loanAmount:number) => void
  onCopySavings,    // (savingsAmount:number) => void
  onClose           // optional close handler
}) {
  // Initialize state from sessionStorage if available
  const [amount, setAmount] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.amount !== undefined && parsed.amount !== "") {
          return formatInputWithCommas(parsed.amount);
        }
      }
    } catch (e) {
      // fallback
    }
    return formatInputWithCommas(initialAmount ?? "");
  });

  const [loanDeduction, setLoanDeduction] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.loanDeduction !== undefined) {
          return formatInputWithCommas(parsed.loanDeduction);
        }
      }
    } catch (e) {
      // fallback
    }
    return "";
  });

  const [applyStampDuty, setApplyStampDuty] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.applyStampDuty !== undefined) return Boolean(parsed.applyStampDuty);
      }
    } catch (e) {
      // fallback
    }
    return true;
  });

  const [stampDutyAmount, setStampDutyAmount] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.stampDutyAmount !== undefined) return String(parsed.stampDutyAmount);
      }
    } catch (e) {
      // fallback
    }
    return "50";
  });

  const [savingsPerc, setSavingsPerc] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.savingsPerc !== undefined) return String(parsed.savingsPerc);
      }
    } catch (e) {
      // fallback
    }
    return "70";
  });

  const [sharesPerc, setSharesPerc] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.sharesPerc !== undefined) return String(parsed.sharesPerc);
      }
    } catch (e) {
      // fallback
    }
    return "30";
  });

  const [copiedKey, setCopiedKey] = useState("");

  // Derived math
  const depNum = parseNum(amount);
  const loanNum = parseNum(loanDeduction);
  const dutyNum = applyStampDuty ? (parseNum(stampDutyAmount) || 50) : 0;
  const totalDeductions = loanNum + dutyNum;
  const isDeficit = depNum > 0 && depNum < totalDeductions;
  const netBalance = Math.max(0, depNum - totalDeductions);

  const sp = parseFloat(savingsPerc) || 0;
  const hp = parseFloat(sharesPerc) || 0;
  const savingsAmt = (netBalance * sp) / 100;
  const sharesAmt = (netBalance * hp) / 100;

  // Persist to sessionStorage whenever inputs change
  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          amount,
          loanDeduction,
          applyStampDuty,
          stampDutyAmount,
          savingsPerc,
          sharesPerc
        })
      );
    } catch (e) {
      // storage unavailable
    }
  }, [amount, loanDeduction, applyStampDuty, stampDutyAmount, savingsPerc, sharesPerc]);

  const handleCopy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(String(text));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1500);
    } catch (e) {
      // ignore
    }
  };

  const handleClear = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    setAmount("");
    setLoanDeduction("");
    setApplyStampDuty(true);
    setStampDutyAmount("50");
    setSavingsPerc("70");
    setSharesPerc("30");
  };

  return (
    <Box sx={{ width: { xs: "100%", sm: 390 }, p: 2.25 }}>
      <Stack spacing={1.75}>
        {/* Top Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0B3B60" }}>
              Quick Split &amp; Stamp Duty Calculator
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
              Sharing formula: Deposit − Loan − Stamp Duty → 70% Savings &amp; 30% Shares
            </Typography>
          </Box>
          <Tooltip title="Clear all calculator figures">
            <Button
              size="small"
              color="error"
              variant="text"
              startIcon={<DeleteOutlineIcon fontSize="small" />}
              onClick={handleClear}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Clear
            </Button>
          </Tooltip>
        </Stack>

        {/* Total Deposit Amount */}
        <TextField
          label="Total Deposit Amount (₦)"
          size="small"
          placeholder="e.g. 4,000,000.00"
          value={amount}
          onChange={(e) => setAmount(formatInputWithCommas(e.target.value))}
          onBlur={() => {
            if (amount) setAmount(formatToCurrencyString(amount));
          }}
          InputProps={{ inputMode: "numeric" }}
          fullWidth
          helperText="Enter gross bank deposit (e.g. 4,000,000.00)"
        />

        {/* Loan Repayment Deduction */}
        <TextField
          label="Loan Repayment Deduction (₦)"
          size="small"
          placeholder="0.00"
          value={loanDeduction}
          onChange={(e) => setLoanDeduction(formatInputWithCommas(e.target.value))}
          onBlur={() => {
            if (loanDeduction) setLoanDeduction(formatToCurrencyString(loanDeduction));
          }}
          InputProps={{ inputMode: "numeric" }}
          fullWidth
          helperText="Portion removed first for loan clearance (optional)"
        />

        {/* Stamp Duty Toggle Card */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: applyStampDuty ? "#F0FDF4" : "#F8FAFC",
            borderColor: applyStampDuty ? "#86EFAC" : "#E2E8F0"
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <VerifiedUserRoundedIcon sx={{ color: applyStampDuty ? "#16A34A" : "#94A3B8", fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                Stamp Duty (EMTL)
              </Typography>
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={applyStampDuty}
                  onChange={(e) => setApplyStampDuty(e.target.checked)}
                  color="success"
                />
              }
              label={applyStampDuty ? `Apply ₦${stampDutyAmount || 50}` : "Waive Duty (₦0)"}
              sx={{ m: 0, "& .MuiTypography-root": { fontWeight: 700, fontSize: "0.8rem" } }}
            />
          </Stack>

          {applyStampDuty && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#475569" }}>
                Preset:
              </Typography>
              {[50, 100, 200].map((preset) => (
                <Button
                  key={preset}
                  size="small"
                  variant={String(stampDutyAmount) === String(preset) ? "contained" : "outlined"}
                  onClick={() => setStampDutyAmount(String(preset))}
                  sx={{
                    minWidth: 54,
                    py: 0.2,
                    px: 0.8,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    borderRadius: "6px",
                    bgcolor: String(stampDutyAmount) === String(preset) ? "#16A34A" : "transparent",
                    color: String(stampDutyAmount) === String(preset) ? "#ffffff" : "#16A34A",
                    borderColor: "#16A34A"
                  }}
                >
                  ₦{preset}
                </Button>
              ))}
            </Stack>
          )}
        </Paper>

        {/* Immediate Alert on Deficit */}
        {isDeficit && (
          <Alert
            severity="error"
            icon={<WarningAmberRoundedIcon />}
            sx={{
              fontWeight: 700,
              fontSize: "0.82rem",
              py: 0.5,
              borderRadius: 2,
              "& .MuiAlert-message": { width: "100%" }
            }}
          >
            Deposit (₦{fNumber(depNum, 2)}) is less than total deductions (₦{fNumber(totalDeductions, 2)})!
            <Box sx={{ fontSize: "0.75rem", fontWeight: 800, mt: 0.5, color: "#991B1B" }}>
              Deficit: ₦{fNumber(totalDeductions - depNum, 2)}
            </Box>
          </Alert>
        )}

        {/* Net Sharing Formula Summary */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: "#F8FAFC",
            borderColor: "#E2E8F0"
          }}
        >
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
              Net Balance to Split:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: "#0B3B60" }}>
              ₦{fNumber(netBalance, 2)}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <TextField
              label="Savings %"
              size="small"
              value={savingsPerc}
              onChange={(e) => setSavingsPerc(e.target.value)}
              sx={{ width: "50%" }}
              inputProps={{ inputMode: "numeric", min: 0, max: 100 }}
            />
            <TextField
              label="Shares %"
              size="small"
              value={sharesPerc}
              onChange={(e) => setSharesPerc(e.target.value)}
              sx={{ width: "50%" }}
              inputProps={{ inputMode: "numeric", min: 0, max: 100 }}
            />
          </Stack>

          {/* Savings Result */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField
              label={`Savings (${savingsPerc || 70}%)`}
              size="small"
              value={`₦${fNumber(savingsAmt, 2)}`}
              InputProps={{ readOnly: true }}
              sx={{ flex: 1 }}
            />
            <Tooltip title={copiedKey === "savings" ? "Copied!" : "Copy Savings"}>
              <IconButton
                aria-label="copy-savings"
                size="small"
                color={copiedKey === "savings" ? "success" : "default"}
                onClick={() => {
                  handleCopy("savings", fNumber(savingsAmt, 2));
                  if (onCopySavings) onCopySavings(savingsAmt);
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Shares Result */}
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label={`Shares (${sharesPerc || 30}%)`}
              size="small"
              value={`₦${fNumber(sharesAmt, 2)}`}
              InputProps={{ readOnly: true }}
              sx={{ flex: 1 }}
            />
            <Tooltip title={copiedKey === "shares" ? "Copied!" : "Copy Shares"}>
              <IconButton
                aria-label="copy-shares"
                size="small"
                color={copiedKey === "shares" ? "success" : "default"}
                onClick={() => handleCopy("shares", fNumber(sharesAmt, 2))}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        <Divider />

        {/* Footer Actions */}
        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
          <Button variant="outlined" size="small" onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>
            Close
          </Button>

          {onApplyLoan && loanNum > 0 && (
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => {
                onApplyLoan(loanNum);
                if (onClose) onClose();
              }}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Apply Loan (₦{fNumber(loanNum, 2)})
            </Button>
          )}

          {onApplySavings && (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => {
                onApplySavings(savingsAmt);
                if (onClose) onClose();
              }}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Apply Savings
            </Button>
          )}

          {onApplyShares && (
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                onApplyShares(sharesAmt);
                if (onClose) onClose();
              }}
              sx={{ textTransform: "none", fontWeight: 700, backgroundColor: "#0B3B60" }}
            >
              Apply Shares
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}